import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import bkstApi from "../../api";
import {
  displayActiveStepToStoredStep,
  getProgressStepsForDisplay,
  hasStoredProgressStep,
  normalizeProductionProgressSteps,
  productionStagesCompleted,
  storedStepToDisplayActiveStep,
} from "../../common/util";
import ProgressRing from "./ProgressRing";
import ProgressStepper from "./ProgressStepper";

export function saveItemProgress(orderId, itemIndex, item, progress) {
  return bkstApi.put(`/my/order/${orderId}/progress`, {
    itemIndex,
    item: {
      productId: item.productId,
      quantity: item.quantity,
      total: item.total,
      variant: item.variant,
    },
    progress,
  });
}

// Clicking a step marks it complete; clicking an already completed step reopens it.
export function getStepAfterClick(clickedStep, currentStep) {
  return clickedStep < currentStep ? +clickedStep : +clickedStep + 1;
}

function getSavedDisplayActiveStep(progress) {
  const productionSteps = normalizeProductionProgressSteps(progress.steps);
  return storedStepToDisplayActiveStep(progress.step, productionSteps.length);
}

const STEP_CONTENT_TEXT =
  "Marking a step complete automatically marks the current + all prior steps complete. Marking a step in-progress only marks all prior steps complete.";

export function ItemProgressBulkActions(props) {
  const { saving, canClear, canMarkDone, onClear, onMarkDone } = props;

  return (
    <>
      <Button
        fullWidth
        variant="text"
        color="error"
        disabled={saving || !canClear}
        onClick={onClear}
      >
        Clear progress
      </Button>
      <Button
        fullWidth
        variant="text"
        color="primary"
        disabled={saving || !canMarkDone}
        onClick={onMarkDone}
      >
        Mark all done
      </Button>
    </>
  );
}

export default function ItemProgress(props) {
  const { orderId, item, itemIndex, onChange, size } = props;

  const [progress, setProgress] = useState(props.progress);
  const [activeStep, setActiveStep] = useState(() => getSavedDisplayActiveStep(props.progress));

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const productionSteps = normalizeProductionProgressSteps(progress.steps);
  const displaySteps = getProgressStepsForDisplay(progress.steps);
  const savedDisplayActive = getSavedDisplayActiveStep(progress);
  const progressStarted = hasStoredProgressStep(progress.step);
  const canMarkDone = productionSteps.length > 0 && savedDisplayActive < productionSteps.length;

  const persistStep = (step, nextDisplayStep) => {
    setSaving(true);
    setError("");
    const data = {
      step,
      steps: productionSteps,
    };
    return saveItemProgress(orderId, itemIndex, item, data)
      .then((res) => {
        setProgress(data);
        setActiveStep(nextDisplayStep);
        if (onChange) {
          onChange(res.data);
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || "An error occurred while saving progress");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const persistDisplayStep = (nextDisplayStep) =>
    persistStep(displayActiveStepToStoredStep(nextDisplayStep), nextDisplayStep);

  const handleContinue = () => {
    const next = Math.min(activeStep + 1, displaySteps.length);
    setActiveStep(next);
    persistDisplayStep(next);
  };

  const handleMarkInProgress = () => {
    persistDisplayStep(activeStep);
  };

  const handleClearProgress = () => {
    persistStep(null, 0);
  };

  const handleMarkDone = () => {
    persistStep(productionSteps.length, productionSteps.length);
  };

  const handleClose = () => {
    setActiveStep(savedDisplayActive);
    setError("");
    setOpen(false);
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <ProgressRing
        pct={
          productionSteps.length
            ? (productionStagesCompleted(progress.step, productionSteps.length) * 100) /
              productionSteps.length
            : 0
        }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        size={size}
      />
      <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth="sm">
        <DialogTitle>Item Progress</DialogTitle>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box my={2}>
            <ProgressStepper
              orientation="vertical"
              steps={displaySteps}
              activeStep={activeStep}
              onContinue={handleContinue}
              onBack={handleMarkInProgress}
              onStepSelect={setActiveStep}
              savedDisplayActive={savedDisplayActive}
              progressStarted={progressStarted}
              actionsDisabled={saving}
              stepContentText={STEP_CONTENT_TEXT}
            />
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <ItemProgressBulkActions
            saving={saving}
            canClear={progressStarted}
            canMarkDone={canMarkDone}
            onClear={handleClearProgress}
            onMarkDone={handleMarkDone}
          />
        </DialogActions>
      </Dialog>
    </span>
  );
}
